import { Box, Button, Card, Chip, Stack, Typography } from '@mui/joy';
import moment from 'moment';
import { dayAfter, localDay } from './calendar';
import type { DispatchEvent } from './calendar';

export function MobileDispatchAgenda({
  events,
  date,
  onSelect,
  onNavigate,
}: {
  events: DispatchEvent[];
  date: Date;
  onSelect: (event: DispatchEvent) => void;
  onNavigate: (date: Date) => void;
}) {
  const first = localDay(moment(date).format('YYYY-MM-DD'));
  const days = Array.from(
    { length: 7 },
    (_, i) => new Date(first.getFullYear(), first.getMonth(), first.getDate() + i),
  );
  const move = (offset: number) =>
    onNavigate(new Date(first.getFullYear(), first.getMonth(), first.getDate() + offset));
  return (
    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
      <Stack direction="row" sx={{ gap: 1, justifyContent: 'space-between', mb: 2 }}>
        <Button size="sm" variant="outlined" onClick={() => move(-7)}>
          Previous Week
        </Button>
        <Button size="sm" variant="outlined" onClick={() => move(7)}>
          Next Week
        </Button>
      </Stack>
      {days.map((day) => {
        const key = moment(day).format('YYYY-MM-DD');
        const records = events
          .filter((e) => e.start < dayAfter(key) && e.end > day)
          .sort((a, b) => a.start.getTime() - b.start.getTime());
        return (
          <Box key={key} sx={{ mb: 2 }}>
            <Typography level="title-lg" sx={{ mb: 1 }}>
              {moment(day).format('ddd, MMM D')}
            </Typography>
            {!records.length && <Typography level="body-sm">No scheduled work</Typography>}
            {[true, false].map((allDay) => {
              const group = records.filter((e) => e.allDay === allDay);
              return (
                group.length > 0 && (
                  <Box key={String(allDay)} sx={{ mb: 1 }}>
                    <Typography level="title-sm">
                      {allDay ? 'All-day work' : 'Timed visits and deadlines'}
                    </Typography>
                    {group.map((event) => (
                      <Card key={event.id} sx={{ my: 1, p: 1.5 }}>
                        <Typography level="title-sm">{event.title}</Typography>
                        {!allDay && (
                          <Typography level="body-sm">
                            {moment(event.start).format('h:mm A')}
                          </Typography>
                        )}
                        <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
                          <Chip size="sm">{event.assigneeName}</Chip>
                          {event.status && (
                            <Chip size="sm">{event.status.replaceAll('_', ' ')}</Chip>
                          )}
                        </Stack>
                        <Button variant="outlined" size="sm" onClick={() => onSelect(event)}>
                          View Details
                        </Button>
                        {event.address && (
                          <Button
                            component="a"
                            target="_blank"
                            rel="noreferrer"
                            variant="plain"
                            size="sm"
                            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(event.address)}`}
                          >
                            Directions
                          </Button>
                        )}
                      </Card>
                    ))}
                  </Box>
                )
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
}
